/** @jsx jsx */

import { Block } from '@chameleoncreator/creator-app';
import { jsx } from '@emotion/core';
import { setExitAction } from 'actions/setExitAction';
import { ErrorModal } from 'components/errorModal';
import Header_v2 from 'components/Header_v2/Header_v2';
import { AudioPlayer } from 'components/Layouts/AudioPlayer';
import { ThemeProvider } from 'emotion-theming';
import { createScorm } from 'factories/scorm';
import { SCORM } from 'pipwerks-scorm-api-wrapper';
import { createContext, useEffect, useLayoutEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  getBlock,
  getInteractionsFromTopicAndState,
  getTopicGroupFromState,
  getTopicsFromGroup,
} from 'selectors/block';
import getInteractionsInTopic from 'services/apiHelpers/getInteractionsInTopic';
import getTopics from 'services/apiHelpers/getTopics';
import setCurrentTopic from 'services/apiHelpers/setCurrentTopic';
import getCurrentLocation from 'services/getCurrentLocation';
import { State } from 'store/store';
import { theme } from 'theme';
import { SCORMRenderer } from '../lib/Scorm/SCORM';
import AssessmentContainer from './AssessmentContainer';
import Bookmark from './Bookmark';
import { FontManager } from './Fonts';
import Interactions_Editor from './Interactions_Editor';
import Interactions_Module from './Interactions_Module';
import { ModuleHasAudioMessage } from './ModuleHasAudioMessage';
import pubSub from 'services/pubSub';
import TopicContainer from './TopicContainer';
import windowScrollInfo from './windowScrollInfo';

// window.location.hash = '';
// console.log(window.location.hash);
if (window.location.hash !== '') {
  // something
}

let moduleHasAudio;
let audioAcknowledged;

export const ModuleContext = createContext({
  passMark: 80,
  exit: () => {},
  setComplete: () => {},
  setCompleteAndExit: () => {},
});

const Module = props => {
  //
  // bookmarkStates
  // 0 no bookmarking
  // 1 pending
  // 2 resolved
  const [bookmarkState, updateBookmarkState] = useState<0 | 1 | 2>(0);
  const [editModuleHasAudioMessage, setEditModuleHasAudioMessage] = useState<
    boolean
  >(false);
  const [, setAudioAcknowledged] = useState<boolean>(window.dev() || false); // double negative ?
  const state = useSelector((state: any) => state) as State;

  let bookmarkingEnabled = state.theme.enableBookmarking;
  if (bookmarkingEnabled === undefined) {
    // if the theme hasn't been manually updated 'bookmarkingEnabled' will be undefined in the theme.js output
    // in that case we interpret undefined as default/true
    bookmarkingEnabled = true;
  }

  // console.log({ bookmarkingEnabled });

  // create re_selectors for these ?
  const { currentTopic: topicId, currentInteraction: interactionId } = state;

  const { blocks, rootBlockId } = state;

  const group = getTopicGroupFromState(state);
  const topicList = getTopicsFromGroup(state, group);

  const dispatch = useDispatch();

  const currentTopicIndex = topicId
    ? topicList.findIndex(t => t.id === topicId)
    : 0;

  useLayoutEffect(() => {
    windowScrollInfo(null, topicId);
  }, [topicId]);

  useEffect(() => {
    if (!window.dev()) {
      return;
    }

    const pS = pubSub.subscribe(
      'SHOW_MODULE_HAS_AUDIO_MESSAGE',
      ({ showModuleHasAudioMessage }) => {
        setEditModuleHasAudioMessage(showModuleHasAudioMessage);
      }
    );

    return () => {
      pS();
    };
  }, []);

  useEffect(() => {
    // put this somewhere else
    const compareIds = (topicInteraction, id) => {
      return [topicInteraction.props.sId, topicInteraction.id]
        .map(sId => String(sId))
        .includes(id);
    };

    const navToHash = e => {
      // console.log('navToHash.starts');

      e.preventDefault();

      let hash: any = String(window.location.hash)
        .replace(/#/, '')
        .split('/');

      // console.log('navToHash.hash', hash);

      if (hash.length !== 2) {
        // console.log('navToHash.returns here');
        // requestAnimationFrame(() => {
        //   (window.locationHistory as any) = null;
        // });
        return;
      }

      // for legacy we'll use the id. format will be the same as
      const [topicSid, interactionSId] = hash;

      const targetTopic = getTopics().find((t: any) => compareIds(t, topicSid));

      // this'll only be for when we're testing
      if (!targetTopic) {
        return;
      }

      const interactionsInTopic = getInteractionsInTopic(targetTopic);

      const targetInteraction = interactionsInTopic.find((i: any) =>
        compareIds(i, interactionSId)
      );

      // be safe. bail if not
      if (!targetInteraction) {
        return;
      }

      // if we're here. we're gonna nav
      // set history
      (window.locationHistory as any) = getCurrentLocation();
      //

      pubSub.publish('MODULE_NAVIGATION', {
        navToHash: 'starts',
      });

      const targetInteractionIndex = interactionsInTopic.findIndex((i: any) =>
        compareIds(i, interactionSId)
      );

      const updatedInteractionsSeen: string[] = [];
      const updatedInteractionsOpen: string[] = [];

      interactionsInTopic.forEach((i: any, index: number) => {
        if (index <= targetInteractionIndex) {
          updatedInteractionsSeen.push(i.id);
          updatedInteractionsOpen.push(i.id);
        }
      });

      dispatch({
        type: 'UPDATE_INTERACTIONS_SEEN',
        data: updatedInteractionsSeen,
      });

      dispatch({
        type: 'UPDATE_INTERACTIONS_OPEN',
        data: updatedInteractionsOpen,
      });

      targetTopic?.id && setCurrentTopic(targetTopic.id);
      window.location.href = `#i${targetInteraction.id}`;
      //

      // console.log('navToHash.ends');

      pubSub.publish('MODULE_NAVIGATION', {
        navToHash: 'ends',
      });
    };

    // only add this listener when NOT in dev
    // means no weirdo nav while editing
    if (!window.dev()) {
      window.addEventListener('hashchange', navToHash);
    }

    // maybe we can toggle all this on / off when scrolling

    return () => {
      window.removeEventListener('hashchange', navToHash);
    };
  }, []);

  useLayoutEffect(() => {
    if (!rootBlockId) {
      return;
    }
    window.scrollTo(0, 0);
  }, [currentTopicIndex]);

  if (!rootBlockId) {
    return null;
  }

  const topic: any = topicList[currentTopicIndex > -1 ? currentTopicIndex : 0];

  // update has with topic name
  // do we want to keep the spaces ' ' > %20
  // assume screen readers etc know how to manage them ( and not read that as literal )
  // const parsedTopicName = topic.props.name.replace(/#/g, '');
  // window.location.hash = parsedTopicName;

  let interactions = topic
    ? getInteractionsFromTopicAndState(state, getBlock(blocks, topic.id))
    : [];

  if (!moduleHasAudio) {
    // const blocks = Object.values(state.blocks) as Block[];
    for (const interaction of interactions) {
      if (
        // interaction.type === 'Interaction' &&
        interaction.props?.includeAudio &&
        interaction.props?.audioSrc
      ) {
        // console.log(interaction.id, interaction.meta?.subType, 'has audio');
        moduleHasAudio = true;
        break;
      }
    }
  }

  let scormBlock = Object.values(blocks).find(
    ((b: Block) => b.type === 'SCORM') as any
  ) as Block;

  if (!scormBlock) {
    scormBlock = createScorm();
  }

  const runExit = () => {
    if (scormBlock.props!.scormControlledExit) {
      dispatch(setExitAction());
    } else {
      dispatch(setExitAction());
      setTimeout(() => {
        window?.top?.close();
      }, 200);
    }
  };

  const moduleBlock = Object.values(blocks).find(
    ((b: Block) => b.type === 'Module') as any
  ) as Block;
  let passMark = moduleBlock.props!.passMark || 80;

  const TopicAssessmentContainer = !topic.props.asAssessment
    ? TopicContainer
    : AssessmentContainer;

  let showModuleHasAudioMessage =
    moduleHasAudio && !audioAcknowledged && bookmarkState !== 1;

  if (window.dev()) {
    showModuleHasAudioMessage = editModuleHasAudioMessage;
  }

  return (
    <ModuleContext.Provider
      value={{
        passMark,
        exit: runExit,
        setComplete() {
          console.log('setComplete()');
          if (SCORM.version === '1.2') {
            SCORM.set('cmi.core.lesson_status', 'completed');
          } else {
            SCORM.set('cmi.completion_status', 'completed');
          }
        },
        setCompleteAndExit() {
          console.log('setCompleteAndExit()');
          if (SCORM.version === '1.2') {
            SCORM.set('cmi.core.lesson_status', 'completed');
          } else {
            SCORM.set('cmi.completion_status', 'completed');
          }

          requestAnimationFrame(() => {
            runExit();
          });
        },
      }}
    >
      <FontManager />
      <ErrorModal>
        {error => (
          <div>
            <h1>Oh no :(</h1>
            <p>Something has gone wrong. The error message provided is:</p>
            <p>
              <strong>"{error.message}".</strong>
            </p>
            <p>
              We have been alerted to this error, however if it is consistently
              occuring please fell free to notify us by clicking on the support
              button in the bottom right hand corner.
            </p>
          </div>
        )}
      </ErrorModal>
      <SCORMRenderer key="scorm" {...scormBlock} />
      <ThemeProvider theme={theme}>
        {/* <Header currentTopicIndex={currentTopicIndex} topics={topicList} /> */}
        {/* {!!interactions.length && (
          <Header_v2
            currentTopicIndex={currentTopicIndex}
            topics={topicList}
          />
        )} */}

        <Header_v2 currentTopicIndex={currentTopicIndex} topics={topicList} />

        <TopicAssessmentContainer
          key={`topic${currentTopicIndex}`}
          state={state}
          topic={topic}
        >
          {window.dev() ? (
            <Interactions_Editor
              currentTopicId={topic.id}
              currentInteractionId={interactionId}
              interactions={interactions}
            />
          ) : (
            <Interactions_Module
              key={interactions[0]?.id}
              interactions={interactions}
            />
          )}
        </TopicAssessmentContainer>

        <div
          id="menu-portal"
          style={{
            position: 'fixed',
            zIndex: 2147483647,
          }}
        />
      </ThemeProvider>

      {showModuleHasAudioMessage && (
        <ModuleHasAudioMessage
          onClose={() => {
            setAudioAcknowledged((audioAcknowledged = true));
          }}
        />
      )}

      {moduleHasAudio && (audioAcknowledged || window.dev()) && (
        <AudioPlayer key={window.dev() ? interactionId : ''} />
      )}

      {bookmarkingEnabled && (
        <Bookmark updateBookmarkState={updateBookmarkState} />
      )}
    </ModuleContext.Provider>
  );
};

export default Module;
